export type MedicationType = "prescribed_course" | "long_term_prescribed" | "self_managed" | "supplement";
export type MedicationStatus = "active" | "completed" | "stopped";
export type Daypart = "morning" | "afternoon" | "evening" | "night";
export type DoseEventStatus = "taken" | "missed" | "skipped";

export type MedicationScheduleSlot = {
  slot_id: string;
  daypart: Daypart | null;
  time: string | null;
  dose_text: string | null;
};

export type MedicationSchedule = {
  slots: MedicationScheduleSlot[];
};

export type Medication = {
  medication_id: string;
  name: string;
  medication_type: MedicationType;
  status: MedicationStatus;
  dose_text: string | null;
  form: string | null;
  strength: string | null;
  prescriber_name: string | null;
  reason: string | null;
  instructions: string | null;
  schedule: MedicationSchedule;
  timezone: string;
  reminder_enabled: boolean;
  start_date: string | null;
  end_date: string | null;
  completed_at: string | null;
  stopped_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type MedicationInput = {
  name: string;
  medication_type: MedicationType;
  dose_text: string | null;
  form: string | null;
  strength: string | null;
  prescriber_name: string | null;
  reason: string | null;
  instructions: string | null;
  schedule: MedicationSchedule;
  timezone: string;
  reminder_enabled: boolean;
  start_date: string | null;
  end_date: string | null;
};

export type UpcomingDose = {
  type: "medication";
  category: "Medication" | "Supplement";
  medication_id: string;
  medication_name: string;
  medication_type: MedicationType;
  occurrence_key: string;
  slot_id: string;
  dose_text: string | null;
  daypart: Daypart | null;
  due_date: string;
  due_time: string | null;
  due_at: string | null;
  timezone: string;
  status: "pending";
};

export type DoseEvent = {
  medication_id: string;
  occurrence_key: string;
  status: DoseEventStatus;
  local_date: string;
  slot_id: string | null;
  daypart: Daypart | null;
  time: string | null;
  dose_text: string | null;
  note: string | null;
  status_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

type MedicationResponse = {
  medication?: Partial<Medication>;
};

type MedicationListResponse = {
  medications?: Array<Partial<Medication>>;
};

type UpcomingDoseListResponse = {
  upcoming_doses?: Array<Partial<UpcomingDose>>;
};

export type MedicationOverview = {
  medications: Medication[];
  upcomingDoses: UpcomingDose[];
};

type MedicationOverviewResponse = {
  medications?: Array<Partial<Medication>>;
  upcoming_doses?: Array<Partial<UpcomingDose>>;
};

type DoseEventResponse = {
  dose_event?: Partial<DoseEvent>;
};

type DoseEventListResponse = {
  dose_events?: Array<Partial<DoseEvent>>;
};

export class MedicationServiceError extends Error {
  constructor(message = "We could not reach your medication records right now.") {
    super(message);
    this.name = "MedicationServiceError";
  }
}

function textOrNull(value: unknown) {
  return typeof value === "string" && value.trim() ? value : null;
}

function normalizeSchedule(schedule: Partial<MedicationSchedule> | undefined): MedicationSchedule {
  const slots = Array.isArray(schedule?.slots) ? schedule.slots : [];
  return {
    slots: slots
      .map((slot) => ({
        slot_id: textOrNull(slot?.slot_id) ?? "",
        daypart: textOrNull(slot?.daypart) as Daypart | null,
        time: textOrNull(slot?.time),
        dose_text: textOrNull(slot?.dose_text),
      }))
      .filter((slot) => slot.slot_id),
  };
}

function normalizeMedication(record: Partial<Medication> | undefined): Medication {
  return {
    medication_id: textOrNull(record?.medication_id) ?? "",
    name: textOrNull(record?.name) ?? "Medication",
    medication_type: (textOrNull(record?.medication_type) as MedicationType | null) ?? "self_managed",
    status: (textOrNull(record?.status) as MedicationStatus | null) ?? "active",
    dose_text: textOrNull(record?.dose_text),
    form: textOrNull(record?.form),
    strength: textOrNull(record?.strength),
    prescriber_name: textOrNull(record?.prescriber_name),
    reason: textOrNull(record?.reason),
    instructions: textOrNull(record?.instructions),
    schedule: normalizeSchedule(record?.schedule),
    timezone: textOrNull(record?.timezone) ?? "UTC",
    reminder_enabled: record?.reminder_enabled === true,
    start_date: textOrNull(record?.start_date),
    end_date: textOrNull(record?.end_date),
    completed_at: textOrNull(record?.completed_at),
    stopped_at: textOrNull(record?.stopped_at),
    created_at: textOrNull(record?.created_at),
    updated_at: textOrNull(record?.updated_at),
  };
}

function normalizeUpcomingDose(record: Partial<UpcomingDose> | undefined): UpcomingDose {
  return {
    type: "medication",
    category: record?.category === "Supplement" ? "Supplement" : "Medication",
    medication_id: textOrNull(record?.medication_id) ?? "",
    medication_name: textOrNull(record?.medication_name) ?? "Medication",
    medication_type: (textOrNull(record?.medication_type) as MedicationType | null) ?? "self_managed",
    occurrence_key: textOrNull(record?.occurrence_key) ?? "",
    slot_id: textOrNull(record?.slot_id) ?? "",
    dose_text: textOrNull(record?.dose_text),
    daypart: textOrNull(record?.daypart) as Daypart | null,
    due_date: textOrNull(record?.due_date) ?? "",
    due_time: textOrNull(record?.due_time),
    due_at: textOrNull(record?.due_at),
    timezone: textOrNull(record?.timezone) ?? "UTC",
    status: "pending",
  };
}

function normalizeDoseEvent(record: Partial<DoseEvent> | undefined): DoseEvent {
  return {
    medication_id: textOrNull(record?.medication_id) ?? "",
    occurrence_key: textOrNull(record?.occurrence_key) ?? "",
    status: (textOrNull(record?.status) as DoseEventStatus | null) ?? "taken",
    local_date: textOrNull(record?.local_date) ?? "",
    slot_id: textOrNull(record?.slot_id),
    daypart: textOrNull(record?.daypart) as Daypart | null,
    time: textOrNull(record?.time),
    dose_text: textOrNull(record?.dose_text),
    note: textOrNull(record?.note),
    status_at: textOrNull(record?.status_at),
    created_at: textOrNull(record?.created_at),
    updated_at: textOrNull(record?.updated_at),
  };
}

async function requestMedication<TBody>(
  apiBaseUrl: string,
  accessToken: string | undefined,
  path: string,
  method: "GET" | "POST" | "PATCH" | "PUT",
  body?: TBody,
  signal?: AbortSignal,
) {
  if (!accessToken) {
    throw new MedicationServiceError("Please sign in again to manage your medication records.");
  }

  const response = await fetch(`${apiBaseUrl}${path}`, {
    method,
    signal,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    if (response.status === 400) {
      throw new MedicationServiceError("Please check the medication details and try again.");
    }

    if (response.status === 401 || response.status === 403) {
      throw new MedicationServiceError("Please sign in again to manage your medication records.");
    }

    if (response.status === 404) {
      throw new MedicationServiceError("We could not find that medication record.");
    }

    throw new MedicationServiceError();
  }

  return (await response.json()) as unknown;
}

export function createMedicationServiceClient(apiBaseUrl: string, getAccessToken: () => string | undefined) {
  return {
    async listMedications(signal?: AbortSignal) {
      const data = (await requestMedication(apiBaseUrl, getAccessToken(), "/v1/medications", "GET", undefined, signal)) as MedicationListResponse;
      return (data.medications ?? []).map(normalizeMedication).filter((medication) => medication.medication_id);
    },
    async createMedication(payload: MedicationInput) {
      const data = (await requestMedication(apiBaseUrl, getAccessToken(), "/v1/medications", "POST", payload)) as MedicationResponse;
      return normalizeMedication(data.medication);
    },
    async updateMedication(medicationId: string, payload: Partial<MedicationInput>) {
      const data = (await requestMedication(
        apiBaseUrl,
        getAccessToken(),
        `/v1/medications/${encodeURIComponent(medicationId)}`,
        "PATCH",
        payload,
      )) as MedicationResponse;
      return normalizeMedication(data.medication);
    },
    async completeMedication(medicationId: string) {
      const data = (await requestMedication(
        apiBaseUrl,
        getAccessToken(),
        `/v1/medications/${encodeURIComponent(medicationId)}/complete`,
        "POST",
      )) as MedicationResponse;
      return normalizeMedication(data.medication);
    },
    async stopMedication(medicationId: string) {
      const data = (await requestMedication(
        apiBaseUrl,
        getAccessToken(),
        `/v1/medications/${encodeURIComponent(medicationId)}/stop`,
        "POST",
      )) as MedicationResponse;
      return normalizeMedication(data.medication);
    },
    async listUpcomingDoses(signal?: AbortSignal) {
      const data = (await requestMedication(apiBaseUrl, getAccessToken(), "/v1/doses/upcoming", "GET", undefined, signal)) as UpcomingDoseListResponse;
      return (data.upcoming_doses ?? []).map(normalizeUpcomingDose).filter((dose) => dose.medication_id && dose.occurrence_key);
    },
    async listMedicationOverview(signal?: AbortSignal): Promise<MedicationOverview> {
      const data = (await requestMedication(apiBaseUrl, getAccessToken(), "/v1/overview", "GET", undefined, signal)) as MedicationOverviewResponse;
      return {
        medications: (data.medications ?? []).map(normalizeMedication).filter((medication) => medication.medication_id),
        upcomingDoses: (data.upcoming_doses ?? []).map(normalizeUpcomingDose).filter((dose) => dose.medication_id && dose.occurrence_key),
      };
    },
    async listDoseEvents(medicationId: string, signal?: AbortSignal) {
      const data = (await requestMedication(
        apiBaseUrl,
        getAccessToken(),
        `/v1/medications/${encodeURIComponent(medicationId)}/dose-events`,
        "GET",
        undefined,
        signal,
      )) as DoseEventListResponse;
      return (data.dose_events ?? []).map(normalizeDoseEvent).filter((event) => event.occurrence_key);
    },
    async putDoseEvent(medicationId: string, occurrenceKey: string, payload: Partial<DoseEvent> & Pick<DoseEvent, "status" | "local_date">) {
      const data = (await requestMedication(
        apiBaseUrl,
        getAccessToken(),
        `/v1/medications/${encodeURIComponent(medicationId)}/dose-events/${encodeURIComponent(occurrenceKey)}`,
        "PUT",
        payload,
      )) as DoseEventResponse;
      return normalizeDoseEvent(data.dose_event);
    },
  };
}
